import { ApiService } from '../apiService'
import { ApiResponse } from '../types/common'
import {
  SessionStateDto,
  SessionListDto,
  JournalEntryDto,
  CreateSessionRequest,
  CreateSessionResponseDto,
  SubmitInputRequest,
  AddJournalNoteRequest,
  AddMapMarkerRequest,
  CastVoteRequest,
  CastVoteResponseDto,
  CastVoteResultDto,
  ProcessResponseDto,
  InputResponseDto,
  VotingProgressDto,
  SessionDebugSummaryDto,
  SessionDebugDetailDto,
  AbandonResponseDto,
  SessionInviteDto,
  CreateSessionInviteRequest,
  ShareCodeResponse,
  SessionParticipantInfo,
  InitiateBootVoteRequest,
  CastBootVoteRequest,
  SessionBootVoteDto,
  MarkJournalReadRequest,
  SessionStartValidationDto,
  AdventureSessionFullDto,
  UpdateSessionSettingsRequest,
} from '../types/session'

// Location update request
export interface UpdateLocationRequest {
  latitude: number
  longitude: number
  accuracy?: number
}

// Presence update request
export interface UpdatePresenceRequest {
  isActive: boolean
}

export const sessionsApi = {
  // ========================
  // Session Management
  // ========================

  // Get sessions for current user. Pass { includeCompleted: true } to also return completed/abandoned.
  getAllSessions: (params?: { includeCompleted?: boolean }) =>
    ApiService.get<SessionListDto[]>('/api/sessions/active', params),

  // Get active session for a specific adventure (to check if user has existing session)
  getActiveSessionForAdventure: (adventureId: string) =>
    ApiService.get<SessionListDto>(`/api/sessions/active/${adventureId}`),

  // Get session by ID
  getSession: (sessionId: string) =>
    ApiService.get<SessionStateDto>(`/api/sessions/${sessionId}`),

  // Create a new session
  // Returns AdventureSessionFullDto directly (201 Created, not wrapped in ApiResponse)
  createSession: (data: CreateSessionRequest) =>
    ApiService.post<AdventureSessionFullDto>('/api/sessions', data),

  // Start a session (moves from NotStarted to InProgress)
  startSession: (sessionId: string) =>
    ApiService.post<ApiResponse<SessionStateDto>>(`/api/sessions/${sessionId}/start`),

  // Get current session state (for polling/resume)
  getSessionState: (sessionId: string) =>
    ApiService.get<SessionStateDto>(`/api/sessions/${sessionId}/state`),

  // Process current node (auto-advance through reward nodes)
  processSession: (sessionId: string) =>
    ApiService.post<ApiResponse<ProcessResponseDto>>(`/api/sessions/${sessionId}/process`),

  // Submit input for a challenge node
  submitInput: (sessionId: string, data: SubmitInputRequest) =>
    ApiService.post<ApiResponse<InputResponseDto>>(`/api/sessions/${sessionId}/input`, data),

  // Transition to a specific edge (for branching)
  transitionSession: (sessionId: string, data: { edgeId: string }) =>
    ApiService.post<ApiResponse<ProcessResponseDto>>(`/api/sessions/${sessionId}/transition`, data),

  // Pause session
  pauseSession: (sessionId: string) =>
    ApiService.post<ApiResponse<void>>(`/api/sessions/${sessionId}/pause`),

  // Resume paused session
  resumeSession: (sessionId: string) =>
    ApiService.post<ApiResponse<SessionStateDto>>(`/api/sessions/${sessionId}/resume`),

  // Abandon session (user wants to quit)
  abandonSession: (sessionId: string) =>
    ApiService.post<ApiResponse<AbandonResponseDto>>(`/api/sessions/${sessionId}/abandon`),

  // ========================
  // Group Session Management
  // ========================

  // Join an existing group session
  joinSession: (sessionId: string) =>
    ApiService.post<ApiResponse<SessionStateDto>>(`/api/sessions/${sessionId}/join`),

  // Leave a group session
  leaveSession: (sessionId: string) =>
    ApiService.post<ApiResponse<void>>(`/api/sessions/${sessionId}/leave`),

  // Update user's location in session
  updateLocation: (sessionId: string, data: UpdateLocationRequest) =>
    ApiService.put<ApiResponse<void>>(`/api/sessions/${sessionId}/location`, data),

  // Update user's presence status
  updatePresence: (sessionId: string, data: UpdatePresenceRequest) =>
    ApiService.post<ApiResponse<void>>(`/api/sessions/${sessionId}/presence`, data),

  // ========================
  // Journal
  // ========================

  // Get journal entries for a session
  getJournal: (sessionId: string, params?: { skip?: number; take?: number }) =>
    ApiService.get<JournalEntryDto[]>(`/api/sessions/${sessionId}/journal`, params),

  // Add a user note to journal
  addJournalNote: (sessionId: string, data: AddJournalNoteRequest) =>
    ApiService.post<ApiResponse<JournalEntryDto>>(`/api/sessions/${sessionId}/journal/note`, data),

  // Add a map marker to journal
  addMapMarker: (sessionId: string, data: AddMapMarkerRequest) =>
    ApiService.post<ApiResponse<JournalEntryDto>>(`/api/sessions/${sessionId}/journal/marker`, data),

  // ========================
  // Voting (Group Sessions)
  // ========================

  // Cast a vote in a voting round
  castVote: (sessionId: string, data: CastVoteRequest) =>
    ApiService.post<ApiResponse<CastVoteResultDto>>(`/api/sessions/${sessionId}/voting/cast`, data),

  // Force-resolve a voting round (admin/moderator)
  forceResolveVoting: (sessionId: string, roundId: string) =>
    ApiService.post<ApiResponse<CastVoteResultDto>>(`/api/sessions/${sessionId}/voting/${roundId}/resolve`),

  // Get voting progress for a round
  getVotingProgress: (sessionId: string, roundId: string) =>
    ApiService.get<VotingProgressDto>(`/api/sessions/${sessionId}/voting/${roundId}/progress`),

  // ========================
  // Invites & Sharing
  // ========================

  // Create an invite for a session
  createInvite: (sessionId: string, data: CreateSessionInviteRequest) =>
    ApiService.post<ApiResponse<SessionInviteDto>>(`/api/sessions/${sessionId}/invites`, data),

  // Get invites for a session
  getInvites: (sessionId: string) =>
    ApiService.get<SessionInviteDto[]>(`/api/sessions/${sessionId}/invites`),

  // Accept an invite
  acceptInvite: (sessionId: string, inviteId: string) =>
    ApiService.post<ApiResponse<void>>(`/api/sessions/${sessionId}/invites/${inviteId}/accept`),

  // Decline an invite (by invite ID)
  declineInvite: (sessionId: string, inviteId: string) =>
    ApiService.post<ApiResponse<void>>(`/api/sessions/${sessionId}/invites/${inviteId}/decline`),

  // Decline an invite for the authenticated user (no invite ID needed)
  declineMyInvite: (sessionId: string) =>
    ApiService.post<ApiResponse<void>>(`/api/sessions/${sessionId}/invites/decline`),

  // Generate a share code for a session
  generateShareCode: (sessionId: string) =>
    ApiService.post<ShareCodeResponse>(`/api/sessions/${sessionId}/share-code`),

  // Toggle anonymity for a session
  toggleAnonymity: (sessionId: string, isAnonymous: boolean) =>
    ApiService.patch<ApiResponse<void>>(`/api/sessions/${sessionId}/anonymity`, { isAnonymous }),

  // Get participants for a session
  getParticipants: (sessionId: string) =>
    ApiService.get<SessionParticipantInfo[]>(`/api/sessions/${sessionId}/participants`),

  // ========================
  // Ready / Kick / Boot Vote
  // ========================

  // Mark self as ready (or unready) in a group session
  markReady: (sessionId: string, ready: boolean = true) =>
    ApiService.post<ApiResponse<void>>(`/api/sessions/${sessionId}/ready?ready=${ready}`),

  // Kick a participant (owner only)
  kickParticipant: (sessionId: string, targetUserId: string, reason?: string) =>
    ApiService.post<ApiResponse<void>>(
      `/api/sessions/${sessionId}/kick/${targetUserId}${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`
    ),

  // Initiate a boot vote against a participant
  initiateBootVote: (sessionId: string, data: InitiateBootVoteRequest) =>
    ApiService.post<ApiResponse<SessionBootVoteDto>>(`/api/sessions/${sessionId}/vote-boot`, data),

  // Cast a vote in an active boot vote
  castBootVote: (sessionId: string, voteId: string, data: CastBootVoteRequest) =>
    ApiService.post<ApiResponse<void>>(`/api/sessions/${sessionId}/vote-boot/${voteId}/cast`, data),

  // Get active boot votes for a session
  getActiveBootVotes: (sessionId: string) =>
    ApiService.get<SessionBootVoteDto[]>(`/api/sessions/${sessionId}/vote-boot`),

  // Mark journal entries as read up to a sequence number
  markJournalRead: (sessionId: string, data: MarkJournalReadRequest) =>
    ApiService.post<ApiResponse<void>>(`/api/sessions/${sessionId}/journal/mark-read`, data),

  // Validate session can be started (checks ready state, proximity, etc.)
  validateSessionStart: (sessionId: string) =>
    ApiService.get<SessionStartValidationDto>(`/api/sessions/${sessionId}/validate-start`),

  // Update session settings (owner only, editable fields)
  updateSessionSettings: (sessionId: string, data: UpdateSessionSettingsRequest) =>
    ApiService.put<{ message: string }>(`/api/sessions/${sessionId}/settings`, data),

  // ========================
  // Debug Endpoints (Maintainers/Owners only)
  // ========================

  // Get all sessions for an adventure (maintainers/owners only)
  getAdventureSessions: (adventureId: string, params?: { includeCompleted?: boolean }) =>
    ApiService.get<SessionDebugSummaryDto[]>(`/api/sessions/adventure/${adventureId}`, params),

  // Get detailed session debug data (maintainers/owners only)
  getSessionDebug: (sessionId: string) =>
    ApiService.get<SessionDebugDetailDto>(`/api/sessions/${sessionId}/debug`),
}
